import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Banknote, Plus, Edit, Trash2, RefreshCcw, CheckCircle, AlertTriangle, AlertCircle,
  Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, 
  XCircle, LayoutGrid, Layers, TrendingUp, DollarSign, FileText, Users,
  List, LayoutList, Shield, CreditCard, Clock
} from 'lucide-react';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import localApiAdapter from '../../core/services/localApiAdapter';
import { useAppData } from '../../core/contexts/AppDataContext';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { SAUDI_BANKS, isValidSaudiIban } from './utils/payrollSaudiFormat';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import '../../shared/styles/GPSTrackingDatePicker.css';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import {
  buildSalairesImportExportProps,
  mapSalairesStatCards,
  renderSalairesBulkDeleteFilterAction,
  SalairesListControlsStrip,
  useSalairesTableSelection,
  SALAIRES_FORM_GRID_CLASS,
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  QawaemTableStickyActionsTd,
  QAWAEM_TABLE_ACTIONS_TH_CLASS,
  useClearSearchOnTableView,
  useSalairesEmployeeOptions,
} from './salairesListPageUtils';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { QawaemErpModalFormTabBar } from '../../products/saudi/components/QawaemErpModalFormTabs';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { useSalairesConfig } from './useSalairesConfig';

const NS = 'modules.salaires.pages.virementsBancairesPage';

const VirementsBancairesPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`${NS}.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const { settings } = useAppData();
  const salariesConfig = useSalairesConfig();
  const entityLabel = p('entity_transfer', { defaultValue: 'تحويل بنكي' });

  const formatAmountDisplay = (val) => {
    if (val == null || val === '') return p('not_available');
    const n = parseFloat(val);
    if (Number.isNaN(n)) return p('not_available');
    return formatBrandMoney(n);
  };

  const fmtDate = (val, opts) => (val ? new Date(val).toLocaleDateString(dateLocale, opts) : p('not_available'));
  const [searchTerm, setSearchTerm] = useState('');
  const [virements, setVirements] = useState([]);

  const generateVirementCode = useCallback(() => {
    const prefix = getEntityPrefix(
      { ...settings, salaries: salariesConfig },
      'payrollTransfer',
      salariesConfig.transferReferencePrefix || 'VIR',
    );
    return generateEntityReference(prefix, virements, (v) => v.code);
  }, [settings, salariesConfig, virements]);

  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  useClearSearchOnTableView(viewMode, searchTerm, setSearchTerm);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [formTab, setFormTab] = useState('general');

  const virementFormTabs = useMemo(() => ([
    { id: 'general', label: p('view_section_transfer'), icon: Banknote, iconClassName: 'text-blue-400' },
    { id: 'amounts', label: p('section_amounts_execution'), icon: DollarSign, iconClassName: 'text-green-400' },
  ]), [p]);
  
  const [formData, setFormData] = useState({ 
    code: '',
    date_virement: '',
    type_virement: '',
    banque: '',
    numero_lot: '',
    nombre_beneficiaires: '',
    montant_total: '',
    statut: '',
    fichier_sif: '',
    date_execution: '',
    periode_paie: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_virements');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  const {
    filters: columnFilters,
    setFilter: setColumnFilter,
    editingKey: columnEditingKey,
    setEditingKey: setColumnEditingKey,
  } = useColumnHeaderFilters();

  const saudiBankOptions = useMemo(() => SAUDI_BANKS.map((b) => ({ value: b.code, label: b.name })), []);
  const [bankAccountOptions, setBankAccountOptions] = useState([]);
  const { employees: payrollEmployees } = useSalairesEmployeeOptions();

  useEffect(() => {
    const loadBankAccounts = async () => {
      try {
        const accountsRes = await localApiAdapter.get('/banking/accounts').catch(() => []);
        const accounts = Array.isArray(accountsRes) ? accountsRes : (accountsRes?.data || []);
        setBankAccountOptions(
          accounts.map((acc) => ({
            value: acc.code || acc.account_code || acc.iban || String(acc.id),
            label: acc.name || acc.account_name || acc.code || acc.iban,
          })),
        );
      } catch (error) {
        console.error('Error loading banking data for transfers:', error);
      }
    };
    loadBankAccounts();
  }, []);

  const mergedBankOptions = useMemo(() => {
    const seen = new Set();
    const merged = [];
    [...bankAccountOptions, ...saudiBankOptions].forEach((opt) => {
      if (!opt?.value || seen.has(opt.value)) return;
      seen.add(opt.value);
      merged.push(opt);
    });
    return merged;
  }, [bankAccountOptions, saudiBankOptions]);

  const fillBeneficiariesFromHr = () => {
    const withIban = payrollEmployees.filter(
      (emp) => emp.iban || emp.numCcp || emp.num_ccp,
    );
    setFormData((prev) => ({
      ...prev,
      nombre_beneficiaires: String(withIban.length),
      notes: withIban
        .map((emp) => {
          const name = `${emp.prenom || ''} ${emp.nom || ''}`.trim();
          const iban = emp.iban || emp.numCcp || emp.num_ccp || '';
          return `${name || emp.mat || emp.id}: ${iban}`;
        })
        .join('\n'),
    }));
  };

  const handleGenerateSif = async (recordId) => {
    try {
      const response = await localApiAdapter.post(`/salaires/virements-bancaires/${recordId}/generate-sif`);
      const data = response?.data || response;
      if (data?.content) {
        const blob = new Blob([data.content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.file_name || 'wps_sif.txt';
        a.click();
        URL.revokeObjectURL(url);
      }
      loadVirements();
    } catch (error) {
      console.error('Error generating SIF:', error);
      alert(p('sif_generate_error'));
    }
  };

  useEffect(() => { loadVirements(); }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadVirements = async () => {
    try {
      const response = await localApiAdapter.get('/salaires/virements-bancaires');
      const list = Array.isArray(response) ? response : (response?.data || []);
      setVirements(sortRecordsNewestFirst(list));
    } catch (error) {
      console.error('Error loading virements:', error);
      setVirements([]);
    }
  };

  const stats = {
    total: virements.length,
    en_attente: virements.filter(v => v.statut === 'en_attente').length,
    execute: virements.filter(v => v.statut === 'execute').length,
    totalBeneficiaires: virements.reduce((sum, v) => sum + (parseInt(v.nombre_beneficiaires) || 0), 0),
    totalMontant: formatBrandMoney(virements.reduce((sum, v) => sum + (parseFloat(v.montant_total) || 0), 0))
  };

  const mainTabs = useMemo(() => [
    { id: 'all_virements', name: t('common.general_view'), icon: LayoutGrid },
    { id: 'par_statut', name: t('common.by_status'), icon: Shield },
    { id: 'par_type', name: t('common.by_type'), icon: CreditCard },
    { id: 'par_banque', name: p('tab_by_bank'), icon: Banknote }
  ], [t, p]);

  const quickFilterButtons = useMemo(() => [
    { id: 'all', name: t('common.all'), icon: Layers },
    { id: 'en_attente', name: p('filter_pending'), icon: Clock },
    { id: 'valide', name: p('filter_validated'), icon: CheckCircle },
    { id: 'execute', name: p('filter_executed'), icon: CheckCircle }
  ], [t, p]);

  const getTypeDistribution = useMemo(() => {
    const salaire = virements.filter(v => v.type_virement === 'salaire').length;
    const prime = virements.filter(v => v.type_virement === 'prime').length;
    const remboursement = virements.filter(v => v.type_virement === 'remboursement').length;
    const autre = virements.filter(v => v.type_virement === 'autre').length;
    return [
      { filterKey: 'salaire', name: p('type_salaries'), count: salaire, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
      { filterKey: 'prime', name: p('type_bonuses'), count: prime, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      { filterKey: 'remboursement', name: p('type_reimbursements'), count: remboursement, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
      { filterKey: 'autre', name: p('type_others'), count: autre, color: 'bg-gradient-to-r from-orange-500 to-orange-600' }
    ];
  }, [virements, p]);

  const virementDistribution = useMemo(() => ({
    all_virements: {
      title: p('dist_overview_title'),
      data: [
        { filterKey: 'en_attente', name: p('dist_pending'), count: stats.en_attente, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'valide', name: p('dist_validated'), count: virements.filter(v => v.statut === 'valide').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'transmis', name: p('dist_transmitted'), count: virements.filter(v => v.statut === 'transmis').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'execute', name: p('dist_executed'), count: stats.execute, color: 'bg-gradient-to-r from-green-500 to-green-600' }
      ]
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { filterKey: 'en_attente', name: p('dist_pending'), count: stats.en_attente, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'valide', name: p('dist_validated'), count: virements.filter(v => v.statut === 'valide').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'transmis', name: p('dist_transmitted'), count: virements.filter(v => v.statut === 'transmis').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'execute', name: p('dist_executed'), count: stats.execute, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'rejete', name: p('dist_rejected'), count: virements.filter(v => v.statut === 'rejete').length, color: 'bg-gradient-to-r from-red-500 to-red-600' }
      ]
    },
    par_type: {
      title: p('dist_by_type_title'),
      data: getTypeDistribution
    },
    par_banque: {
      title: p('dist_by_bank_title'),
      data: Array.from(new Set(virements.map(v => v.banque).filter(Boolean))).map(banque => ({
        filterKey: banque,
        name: banque,
        count: virements.filter(v => v.banque === banque).length,
        color: 'bg-gradient-to-r from-blue-500 to-blue-600'
      }))
    }
  }), [virements, stats, p, getTypeDistribution]);

  const distributionInfo = virementDistribution[mainViewMode] || virementDistribution.all_virements;

  const statusOptions = useMemo(() => [
    { value: 'en_attente', label: p('status_pending'), icon: Clock, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'valide', label: p('status_validated'), icon: CheckCircle, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'transmis', label: p('status_transmitted'), icon: CheckCircle, style: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    { value: 'execute', label: p('status_executed'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'rejete', label: p('status_rejected'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' }
  ], [p]);

  const transferTypeOptions = useMemo(() => ([
    { value: 'salaire', label: p('type_salary') },
    { value: 'prime', label: p('type_bonus') },
    { value: 'remboursement', label: p('type_reimbursement') },
    { value: 'autre', label: p('type_other') },
  ]), [p]);

  const getTransferTypeLabel = (value) =>
    transferTypeOptions.find((o) => o.value === value)?.label || value || p('not_available');

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('code', (row) => row.code || ''),
      textColumnRule('date_virement', (row) => fmtDate(row.date_virement, { day: '2-digit', month: '2-digit', year: 'numeric' })),
      textColumnRule('nombre_beneficiaires', (row) => String(row.nombre_beneficiaires ?? '')),
      textColumnRule('montant_total', (row) => String(row.montant_total ?? '')),
      exactColumnRule('statut', (row) => row.statut || ''),
    ],
    [dateLocale],
  );

  const colHeaderFilterProps = (key, label, extra = {}) => ({
    columnKey: key,
    label,
    value: columnFilters[key] ?? '',
    onChange: (v) => setColumnFilter(key, v),
    editingKey: columnEditingKey,
    onEditingKeyChange: setColumnEditingKey,
    thClassName: 'text-white font-semibold',
    ...extra,
  });

  const filteredVirements = useMemo(() => {
    const base = virements.filter(v => {
    if (searchTerm && !v.code?.toLowerCase().includes(searchTerm.toLowerCase()) && !v.banque?.toLowerCase().includes(searchTerm.toLowerCase()) && !v.numero_lot?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (mainViewMode === 'all_virements') {
      if (quickFilter === 'en_attente' && v.statut !== 'en_attente') return false;
      if (quickFilter === 'valide' && v.statut !== 'valide') return false;
      if (quickFilter === 'execute' && v.statut !== 'execute') return false;
    }
    if (activeDistributionFilter) {
      switch (mainViewMode) {
        case 'all_virements':
        case 'par_statut':
          if (activeDistributionFilter !== v.statut) return false;
          break;
        case 'par_type':
          if (activeDistributionFilter !== v.type_virement) return false;
          break;
        case 'par_banque':
          if (activeDistributionFilter !== v.banque) return false;
          break;
      }
    }
    return true;
  });

    const filtered = applyColumnFilters(base, columnFilters, columnFilterRules);
    return [...filtered].sort((a, b) => b.id - a.id);
  }, [virements, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, columnFilters, columnFilterRules]);

  const {
    selectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
    clearSelection,
  } = useSalairesTableSelection(filteredVirements);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.date_virement) {
      notifyError(showNotify, p('alert_date_required', { defaultValue: p('field_transfer_date') }));
      return;
    }
    if (!formData.banque) {
      notifyError(showNotify, p('alert_bank_required', { defaultValue: p('field_bank') }));
      return;
    }
    try {
      const code = (formData.code || '').trim() || generateVirementCode();
      const submitData = {
        ...formData,
        code,
        statut: formData.statut || 'en_attente',
        type_virement: formData.type_virement || 'salaire',
        nombre_beneficiaires: parseInt(formData.nombre_beneficiaires, 10) || 0,
        montant_total: parseFloat(formData.montant_total) || 0,
        fichier_sepa: formData.fichier_sif || formData.fichier_sepa || '',
      };
      if (editingRecord) {
        await localApiAdapter.put(`/salaires/virements-bancaires/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/salaires/virements-bancaires', submitData);
        notifyCreated(showNotify, entityLabel);
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('save_error'));
    } finally {
      resetForm();
      loadVirements();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormTab('general');
    setFormData({ ...record });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_transfer_confirm'))) {
      try {
        await localApiAdapter.delete(`/salaires/virements-bancaires/${id}`);
        notifyDeleted(showNotify, entityLabel);
        loadVirements();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('save_error'));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_transfers_bulk', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/salaires/virements-bancaires/${id}`)));
        clearSelection();
        notifyBulkDeleted(showNotify, deleteCount, entityLabel);
        loadVirements();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('save_error'));
      }
    }
  };

  const resetForm = () => {
    setFormTab('general');
    setFormData({ 
      code: '', date_virement: '', type_virement: '', banque: '', numero_lot: '',
      nombre_beneficiaires: '', montant_total: '', statut: '', fichier_sif: '',
      date_execution: '', periode_paie: '', notes: ''
    });
    setEditingRecord(null);
    setShowModal(false);
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      const record = virements.find((v) => v.id === recordId);
      if (!record) return;
      await localApiAdapter.put(`/salaires/virements-bancaires/${recordId}`, { ...record, statut: newStatus });
      await loadVirements();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const listStats = mapSalairesStatCards([
    { key: 'total', label: p('stat_total'), value: stats.total, icon: Banknote },
    { key: 'pending', label: p('stat_pending'), value: stats.en_attente, icon: Clock },
    { key: 'executed', label: p('stat_executed'), value: stats.execute, icon: CheckCircle },
    { key: 'beneficiaries', label: p('stat_beneficiaries'), value: stats.totalBeneficiaires, icon: Users },
  ]);

  const importExportProps = buildSalairesImportExportProps({
    showImportDropdown,
    setShowImportDropdown,
    showExportDropdown,
    setShowExportDropdown,
    importDropdownRef,
    exportDropdownRef,
    p,
    t,
  });

  const qawaemBaseColumns = useMemo(() => ensureStatusBeforeActions([
    { key: 'code', label: p('col_code'), required: true },
    { key: 'date_virement', label: p('col_transfer_date') },
    { key: 'banque', label: p('col_bank') },
    { key: 'nombre_beneficiaires', label: p('col_beneficiaries') },
    { key: 'montant_total', label: p('col_total_amount') },
    { key: 'statut', label: t('common.status') },
  ]), [p, t]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, t('common.actions')),
    [pickerColumns, t],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol),
    [qawaemBaseColumns, showCol],
  );

  const advancedOptionsContent = (
    <div className="space-y-4 pt-4">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button key={tab.id} type="button" onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
              <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>
      {mainViewMode === 'all_virements' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
          <div className="flex space-x-1 overflow-x-auto table-scroll">
            {quickFilterButtons.map((filter) => (
              <button key={filter.id} type="button" onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
          {activeDistributionFilter && (
            <button type="button" onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
              <XCircle size={14} />{p('reset_filter')}
            </button>
          )}
        </div>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
          {distributionInfo.data.map((item) => (
            <button key={item.filterKey || item.name} type="button" onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.filterKey || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
              <div className="flex justify-between items-center mb-2">
                <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <FacturationListPageLayout
        className="space-y-3"
        containerClassName="employees-container"
        title={p('title')}
        subtitle={p('subtitle')}
        icon={Banknote}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={renderSalairesBulkDeleteFilterAction({
          selectedCount: selectedRecords.length,
          onBulkDelete: handleBulkDelete,
          t,
        })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
        onCreate={() => {
          setEditingRecord(null);
          setFormTab('general');
          const today = new Date().toISOString().slice(0, 10);
          setFormData({
            code: generateVirementCode(),
            date_virement: today,
            type_virement: 'salaire',
            banque: '',
            numero_lot: '',
            nombre_beneficiaires: '',
            montant_total: '',
            statut: 'en_attente',
            fichier_sif: '',
            date_execution: '',
            periode_paie: '',
            notes: '',
          });
          setShowModal(true);
        }}
        createLabel={p('new_transfer')}
        importExport={importExportProps}
        headerActions={
          <button type="button" onClick={loadVirements} className="refresh-button" title={t('common.refresh')}>
            <RefreshCcw size={20} />
          </button>
        }
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={t('common.advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={
          <SalairesListControlsStrip
            hidden={qawaem}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
            isDashboardLightMode={isDashboardLightMode}
            cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
            tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
          />
        }
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {showCol('code') ? <TableColumnHeaderFilter {...colHeaderFilterProps('code', p('col_code'))} /> : null}
                  {showCol('date_virement') ? <TableColumnHeaderFilter {...colHeaderFilterProps('date_virement', p('col_transfer_date'))} /> : null}
                  {showCol('banque') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_bank')}</th> : null}
                  {showCol('nombre_beneficiaires') ? <TableColumnHeaderFilter {...colHeaderFilterProps('nombre_beneficiaires', p('col_beneficiaries'), { align: 'center' })} /> : null}
                  {showCol('montant_total') ? <TableColumnHeaderFilter {...colHeaderFilterProps('montant_total', p('col_total_amount'))} /> : null}
                  {showCol('statut') ? (
                  <TableColumnHeaderFilter
                    {...colHeaderFilterProps('statut', t('common.status'), {
                      align: 'center',
                      kind: 'select',
                      options: statusOptions.map((o) => ({ value: o.value, label: o.label })),
                    })}
                  />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={visibleColumnCount}
                    totalCount={qawaemColumns.length}
                    thClassName={QAWAEM_TABLE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredVirements.length > 0 ? (
                  filteredVirements.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(record.id)}
                        onChange={() => handleSelectRecord(record.id)}
                      />
                      {showCol('code') ? (
                      <td className="px-6 py-4"><span className="text-white font-semibold">{record.code}</span></td>
                      ) : null}
                      {showCol('date_virement') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5 text-white font-semibold">
                            <Clock size={14} className="text-white/50" />
                            {fmtDate(record.date_virement, { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </div>
                          <span className="text-white/50 text-xs mt-0.5">{record.periode_paie || p('not_available')}</span>
                        </div>
                      </td>
                      ) : null}
                      {showCol('banque') ? (
                      <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.banque}</span></td>
                      ) : null}
                      {showCol('nombre_beneficiaires') ? (
                      <td className="px-6 py-4 text-center"><span className="text-white font-bold text-lg">{record.nombre_beneficiaires || 0}</span></td>
                      ) : null}
                      {showCol('montant_total') ? (
                      <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-bold text-lg">{formatAmountDisplay(record.montant_total)}</span></div></td>
                      ) : null}
                      {showCol('statut') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={record.statut}
                            recordId={record.id}
                            options={statusOptions}
                            onChange={handleStatusChange}
                            title={p('change_status_title', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
                          />
                        </div>
                      </td>
                      ) : null}
                      <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </QawaemTableStickyActionsTd>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={visibleTableColCount} className="text-center py-12 text-white/60"><Banknote size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={visibleTableColCount} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_total', { count: filteredVirements.length })}</span>
                      <span>{p('footer_beneficiaries')} <span className="text-white font-semibold">{filteredVirements.reduce((sum, v) => sum + (parseInt(v.nombre_beneficiaires) || 0), 0)}</span></span>
                      <span>{p('footer_total_amount')} <span className="text-white font-semibold">{formatAmountDisplay(filteredVirements.reduce((sum, v) => sum + (parseFloat(v.montant_total) || 0), 0))}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredVirements.length > 0 ? (
              filteredVirements.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><Banknote size={20} className="text-emerald-400" />{record.code}</h3>
                        <div className="flex"><QawaemClickableStatusBadge status={record.statut} recordId={record.id} options={statusOptions} onChange={handleStatusChange} /></div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_section_transfer')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_code')}</span> {record.code}</div>
                            <div><span className="font-medium">{p('label_date')}</span> {fmtDate(record.date_virement)}</div>
                            <div><span className="font-medium">{p('label_type')}</span> {getTransferTypeLabel(record.type_virement)}</div>
                            <div><span className="font-medium">{p('label_period')}</span> {record.periode_paie || p('not_available')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_section_bank')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_bank')}</span> {record.banque}</div>
                            <div><span className="font-medium">{p('label_lot')}</span> {record.numero_lot || p('not_available')}</div>
                            <div><span className="font-medium">{p('label_sif_file')}</span> {record.fichier_sif || record.fichier_sepa || p('not_available')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_section_amounts')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_beneficiaries')}</span> <span className="text-white font-bold text-lg">{record.nombre_beneficiaires || 0}</span></div>
                            <div><span className="font-medium text-green-400">{p('label_total_amount')}</span> <span className="text-white font-bold text-lg">{formatAmountDisplay(record.montant_total)}</span></div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_section_execution')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_execution_date')}</span> {fmtDate(record.date_execution)}</div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{p('label_status')}</span>
                              <QawaemClickableStatusBadge status={record.statut} recordId={record.id} options={statusOptions} onChange={handleStatusChange} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><Banknote size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
        open={showViewModal}
        onClose={() => { setShowViewModal(false); setViewingRecord(null); }}
        title={p('view_title')}
        icon={Eye}
        testId="virements-view-modal"
      >
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Banknote size={18} className="text-emerald-400" />{p('view_section_transfer')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className="text-white/60 text-sm mb-1">{p('col_code')}</p><p className="text-white font-medium">{viewingRecord.code}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('col_transfer_date')}</p><p className="text-white font-medium">{fmtDate(viewingRecord.date_virement)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('col_type')}</p><p className="text-white font-medium">{getTransferTypeLabel(viewingRecord.type_virement)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('view_pay_period')}</p><p className="text-white font-medium">{viewingRecord.periode_paie || p('not_available')}</p></div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4">{p('view_section_bank_file')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-white/60 text-sm mb-1">{p('col_bank')}</p><p className="text-white font-medium">{viewingRecord.banque}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('view_lot_number')}</p><p className="text-white font-medium">{viewingRecord.numero_lot || p('not_available')}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('view_sif_file')}</p><p className="text-white font-medium">{viewingRecord.fichier_sif || viewingRecord.fichier_sepa || p('not_available')}</p></div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <TrendingUp size={18} className="text-blue-400" />
                      {p('view_section_amounts')}
                    </h3>
                    <div className="space-y-4">
                      <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/20">
                        <p className="text-white/60 text-sm mb-1">{p('view_beneficiary_count')}</p>
                        <p className="text-white font-bold text-2xl">{viewingRecord.nombre_beneficiaires || 0}</p>
                      </div>
                      <div className="bg-emerald-500/10 rounded-lg p-4 border border-emerald-500/20">
                        <p className="text-white/60 text-sm mb-1">{p('col_total_amount')}</p>
                        <p className="text-emerald-400 font-bold text-2xl">{formatAmountDisplay(viewingRecord.montant_total)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
      </QawaemErpViewModal>
      ) : null}

      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
              <h2 className="text-2xl font-bold text-white">{editingRecord ? p('modal_edit') : p('modal_create')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="virement-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><CheckCircle size={18} />{editingRecord ? p('update') : p('create')}</button>
                <button type="button" onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>

            <QawaemErpModalFormTabBar tabs={virementFormTabs} activeTab={formTab} onTabChange={setFormTab} isLight={isDashboardLightMode} ariaLabel={editingRecord ? p('modal_edit') : p('modal_create')} />
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="virement-form" onSubmit={handleSubmit}>
                {formTab === 'general' && (
                  <div className={SALAIRES_FORM_GRID_CLASS}>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_code')}</label><input type="text" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('col_code')} /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('field_transfer_date')} required /></label><CustomDatePicker value={formData.date_virement} onChange={(value) => setFormData({ ...formData, date_virement: value })} placeholder={t('common.select')} /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('field_transfer_type')} required /></label><CustomSelect value={formData.type_virement} onChange={(value) => setFormData({ ...formData, type_virement: value })} options={transferTypeOptions} autoSelect={false} searchable creatable /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_pay_period')}</label><input type="text" value={formData.periode_paie} onChange={(e) => setFormData({ ...formData, periode_paie: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_pay_period')} /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_status')}</label><CustomSelect value={formData.statut} onChange={(value) => setFormData({ ...formData, statut: value })} options={statusOptions.map(s => ({ value: s.value, label: s.label, icon: <s.icon size={14} className={s.style.split(' ')[1]} /> }))} autoSelect={false} searchable creatable /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_lot_number')}</label><input type="text" value={formData.numero_lot} onChange={(e) => setFormData({ ...formData, numero_lot: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_lot')} /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('field_bank')} required /></label><CustomSelect value={formData.banque} onChange={(value) => setFormData({ ...formData, banque: value })} options={mergedBankOptions} autoSelect={false} searchable creatable placeholder={p('ph_bank')} /></div>
                    <div className="min-w-0 flex items-end">
                      <button type="button" onClick={fillBeneficiariesFromHr} className="w-full px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white text-sm hover:bg-white/15 transition-colors">
                        {p('btn_import_hr_beneficiaries', { defaultValue: 'استيراد المستفيدين من الموارد البشرية' })}
                      </button>
                    </div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_sif_file')}</label><input type="text" value={formData.fichier_sif} onChange={(e) => setFormData({ ...formData, fichier_sif: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_sif')} /></div>
                  </div>
                )}

                {formTab === 'amounts' && (
                  <div className={SALAIRES_FORM_GRID_CLASS}>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_beneficiary_count')}</label><CustomNumberInput step={1} value={formData.nombre_beneficiaires || ''} onChange={(value) => setFormData({ ...formData, nombre_beneficiaires: value })} placeholder="0" /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_total_amount')}</label><CustomNumberInput step={0.01} value={formData.montant_total || ''} onChange={(value) => setFormData({ ...formData, montant_total: value })} placeholder="0.00" /></div>
                    <div className="min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_execution_date')}</label><CustomDatePicker value={formData.date_execution} onChange={(value) => setFormData({ ...formData, date_execution: value })} placeholder={t('common.select')} /></div>
                    <div className="md:col-span-3 min-w-0"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_notes')}</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_notes')} /></div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default VirementsBancairesPage;


